import React, { useEffect, useState, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Unlock, Pencil, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { statusMeta } from "@/lib/status";
import StatusPill from "@/components/StatusPill";
import { InlineLoader } from "@/components/Spinner";

const ACTION_COLOR = {
  sold: "text-sky-700 bg-sky-50",
  sold_again: "text-orange-700 bg-orange-50",
  reopened: "text-stone-700 bg-stone-100",
  edited: "text-violet-700 bg-violet-50",
  deleted: "text-red-700 bg-red-50",
  layout_updated: "text-stone-600 bg-stone-100",
};

export default function AdminBaldachinDrawer({ baldachinId, date, open, onOpenChange, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [editUserId, setEditUserId] = useState("");
  const [editNote, setEditNote] = useState("");

  const load = useCallback(() => {
    if (!baldachinId) return;
    setLoading(true);
    api
      .get(`/baldachins/${baldachinId}/detail${date ? `?date=${date}` : ""}`)
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [baldachinId, date]);

  useEffect(() => {
    if (open && baldachinId) load();
  }, [open, baldachinId, load]);

  const refreshAll = () => {
    load();
    onChanged?.();
  };

  const doReopen = async () => {
    try {
      await api.post(`/baldachins/${baldachinId}/reopen`);
      toast.success("Baldachin reopened and set as Free");
      setConfirmReopen(false);
      refreshAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/actions/${deleteTarget.id}`);
      toast.success("Record deleted");
      setDeleteTarget(null);
      refreshAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openEdit = async (row) => {
    setEditTarget(row);
    setEditNote(row.note || "");
    try {
      const r = await api.get("/workers");
      setWorkers(r.data);
      const me = r.data.find((w) => w.name === row.user_name);
      setEditUserId(me ? me.id : "");
    } catch {
      setWorkers([]);
    }
  };

  const doEdit = async () => {
    try {
      await api.put(`/actions/${editTarget.id}`, { user_id: editUserId || null, note: editNote });
      toast.success("Record updated");
      setEditTarget(null);
      refreshAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const meta = detail ? statusMeta(detail.status) : null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="baldachin-drawer">
          {loading || !detail ? (
            <>
              <SheetTitle className="sr-only">Baldachin details</SheetTitle>
              <InlineLoader label="Loading details…" />
            </>
          ) : (
            <>
              <SheetHeader className="text-left">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-2xl font-heading text-2xl font-black"
                    style={{ background: meta.bg, color: meta.text, border: `2px solid ${meta.border}` }}
                  >
                    {detail.baldachin.number}
                  </div>
                  <div>
                    <SheetTitle className="font-heading text-xl">Baldachin {detail.baldachin.number}</SheetTitle>
                    <SheetDescription>
                      {detail.baldachin.zone || "No zone"} · {detail.is_today ? "Today" : detail.date}
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-stone-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Status</p>
                  <div className="mt-1.5"><StatusPill status={detail.status} /></div>
                </div>
                <div className="rounded-xl border border-stone-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Sales today</p>
                  <p className="mt-1 font-heading text-2xl font-black">{detail.count}x</p>
                </div>
              </div>

              {detail.is_today && (
                <div className="mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setConfirmReopen(true)}
                    data-testid="reopen-baldachin-btn"
                    className="w-full justify-center gap-2 rounded-xl border-stone-300 font-semibold"
                  >
                    <Unlock className="h-4 w-4" /> Reopen / Set as Free
                  </Button>
                </div>
              )}

              <div className="mt-6">
                <p className="mb-2 text-sm font-bold">Action history — {detail.is_today ? "today" : detail.date}</p>
                {detail.history.length === 0 ? (
                  <p className="rounded-xl bg-stone-50 px-3 py-6 text-center text-sm text-stone-400" data-testid="drawer-empty-history">
                    No actions recorded.
                  </p>
                ) : (
                  <ul className="space-y-2" data-testid="drawer-history-list">
                    {detail.history.map((h) => (
                      <li key={h.id} className="rounded-xl border border-stone-200 p-3" data-testid={`history-row-${h.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${ACTION_COLOR[h.action_type] || "bg-stone-100"}`}>
                                {h.action_label}
                              </span>
                              <span className="flex items-center gap-1 text-xs text-stone-500">
                                <Clock className="h-3 w-3" /> {h.time}
                              </span>
                            </div>
                            <p className="mt-1 text-sm font-medium">
                              {h.user_name} <span className="text-xs text-stone-400">({h.user_role})</span>
                            </p>
                            {h.note && <p className="mt-0.5 text-xs text-stone-500">{h.note}</p>}
                          </div>
                          {h.editable && (
                            <div className="flex shrink-0 gap-1">
                              <button
                                onClick={() => openEdit(h)}
                                data-testid={`edit-record-${h.id}`}
                                className="flex h-8 w-8 items-center justify-center rounded-lg text-stone-500 transition hover:bg-stone-100"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(h)}
                                data-testid={`delete-record-${h.id}`}
                                className="flex h-8 w-8 items-center justify-center rounded-lg text-red-500 transition hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmReopen} onOpenChange={setConfirmReopen}>
        <AlertDialogContent data-testid="confirm-reopen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen this baldachin?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set the baldachin back to <b>Free</b> and reset today's sale count. The action is recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-reopen">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doReopen} data-testid="confirm-reopen">Reopen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && `Deleting "${deleteTarget.action_label}" by ${deleteTarget.user_name} at ${deleteTarget.time}.`}{" "}
              This is permanent and will be logged. The sale count will be recalculated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="edit-record-dialog">
          <DialogHeader>
            <DialogTitle>Edit sale record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Assigned worker</Label>
              <Select value={editUserId} onValueChange={setEditUserId}>
                <SelectTrigger data-testid="edit-worker-select"><SelectValue placeholder="Select worker" /></SelectTrigger>
                <SelectContent>
                  {workers.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Note (optional)</Label>
              <Input value={editNote} onChange={(e) => setEditNote(e.target.value)} data-testid="edit-note-input" placeholder="Reason for the change" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} data-testid="cancel-edit">Cancel</Button>
            <Button onClick={doEdit} data-testid="save-edit">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
